/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Verifies a COSE_Sign1 claim signature with WebCrypto: builds the RFC 8152
 * Sig_structure over the (detached) claim payload and checks it against the
 * signing certificate's public key.
 */

import { encodeArray, encodeBytes, encodeText } from '../cbor/encode'
import { algSpec } from './alg'
import { coseAlg, type CoseSign1 } from './decodeSign1'

/** Builds the COSE `Sig_structure` bytes for a Signature1 context. */
export function sigStructure (protectedRaw: Uint8Array, externalAad: Uint8Array, payload: Uint8Array): Uint8Array {
    return encodeArray([
        encodeText('Signature1'),
        encodeBytes(protectedRaw),
        encodeBytes(externalAad),
        encodeBytes(payload)
    ])
}

/**
 * Verifies a detached-payload COSE_Sign1 against `spkiDer` (the signer's
 * SubjectPublicKeyInfo). `payload` is the claim's raw CBOR bytes. Returns false
 * for unsupported algorithms or any verification failure (never throws).
 */
export async function verifyCoseSign1 (sign1: CoseSign1, payload: Uint8Array, spkiDer: Uint8Array): Promise<boolean> {
    const alg = coseAlg(sign1)
    if (alg === undefined) return false
    const spec = algSpec(alg)
    if (!spec) return false

    try {
        const key = await crypto.subtle.importKey('spki', spkiDer as unknown as ArrayBuffer, spec.importParams, false, ['verify'])
        const tbs = sigStructure(sign1.protectedRaw, new Uint8Array(0), payload)
        return await crypto.subtle.verify(spec.verifyParams, key, sign1.signature as unknown as ArrayBuffer, tbs as unknown as ArrayBuffer)
    } catch {
        return false
    }
}
